import { useTranslation } from 'react-i18next'
import { ShoppingBag, DollarSign, Clock, TrendingUp } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { formatCurrency, formatNumber } from '@/lib/formatters'
import type { DashboardStats } from '../types'

interface QuickStatsCardsProps {
  stats: DashboardStats
  isLoading?: boolean
}

interface StatCardProps {
  title: string
  value: string
  icon: React.ReactNode
  highlight?: boolean
}

function StatCard({ title, value, icon, highlight }: StatCardProps) {
  return (
    <Card className={highlight ? 'border-amber-300 bg-amber-50' : ''}>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold mt-1">{value}</p>
          </div>
          <div
            className={`h-12 w-12 rounded-full flex items-center justify-center ${
              highlight
                ? 'bg-amber-200 text-amber-700'
                : 'bg-primary/10 text-primary'
            }`}
          >
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

function StatCardSkeleton() {
  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="h-4 w-24 bg-muted animate-pulse rounded" />
            <div className="h-8 w-20 bg-muted animate-pulse rounded" />
          </div>
          <div className="h-12 w-12 rounded-full bg-muted animate-pulse" />
        </div>
      </CardContent>
    </Card>
  )
}

export function QuickStatsCards({ stats, isLoading }: QuickStatsCardsProps) {
  const { t } = useTranslation('dashboard')

  if (isLoading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCardSkeleton />
        <StatCardSkeleton />
        <StatCardSkeleton />
        <StatCardSkeleton />
      </div>
    )
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <StatCard
        title={t('stats.todayOrders')}
        value={formatNumber(stats.todayOrderCount)}
        icon={<ShoppingBag className="h-6 w-6" />}
      />
      <StatCard
        title={t('stats.todayRevenue')}
        value={formatCurrency(stats.todayRevenue)}
        icon={<DollarSign className="h-6 w-6" />}
      />
      <StatCard
        title={t('stats.pendingOrders')}
        value={formatNumber(stats.pendingOrdersCount)}
        icon={<Clock className="h-6 w-6" />}
        highlight={stats.pendingOrdersCount > 0}
      />
      <StatCard
        title={t('stats.averageOrder')}
        value={formatCurrency(stats.averageOrderValue)}
        icon={<TrendingUp className="h-6 w-6" />}
      />
    </div>
  )
}
